import { gqlTagGira } from '@atlassian/jira-issue-fetch-services-common/src/services/issue-gira-data/gira-fragments/index.tsx';
import { getClient } from '@atlassian/jira-issue-fetch-services/src/common/utils/client.tsx';
import {
	toErrorId,
	toErrorMessage,
} from '@atlassian/jira-project-settings-apps-common/src/types.tsx';
import { famaCatchAndLogFetchError } from '@atlassian/jira-project-settings-apps-common/src/util/catch-and-log-fetch-error.tsx';
import type { ProjectContext } from '@atlassian/jira-router-resources-project-context/src/services/project-context/types.tsx';
import { toBaseUrl } from '@atlassian/jira-shared-types/src/general.tsx';
import {
	PACKAGE_NAME,
	type Group,
	DATES_GROUP,
	LABELS_GROUP,
	NUMBER_GROUP,
	PEOPLE_GROUP,
	SELECT_GROUP,
	SYSTEM_GROUP,
	TEXT_GROUP,
	ADVANCED_GROUP,
	OTHER_GROUP,
	SUGGESTED_GROUP,
	ASSETS_GROUP,
	VERSION_GROUP,
	RESTRICTED_GROUP,
	AI_SUGGESTED_GROUP,
} from '../../constants.tsx';
import type { FetchAvailableItemsParams, FetchMoreAvailableItemsParams } from '../../types.tsx';
import type { ExtraDefinerId } from '../../types/issue/issue-layout-owner.tsx';
import type { AvailableLayoutItemGroup } from '../../types/layout-items.tsx';
import { AvailableLayoutItemGroupsFragments } from '../fragments/index.tsx';
import { transformAvailableLayoutItemGroups } from '../transformer/available-layout-item-groups.tsx';
import type { GraphqlResponse } from '../types.tsx';
import type { AvailableIssueLayoutItems } from './__generated_apollo__/AvailableIssueLayoutItems';

// remove the $isNumberFieldConfigurableEnabled argument from the query when cleaning up the FG frontier-currency-field-d1
const availableItems = gqlTagGira`
query AvailableIssueLayoutItems(
    $projectId: Long!,
    $extraDefinerId: Long!,
    $layoutType: IssueLayoutType!,
    $pageSize: Int!,
    $queryString: String!,
    $fieldPropertyKeys: [String!]!,
    $after: String,
    $includeRestrictedItems: Boolean = true,
    $includeSystemItems: Boolean = true,
    $includeTextItems: Boolean = true,
    $includeLabelsItems: Boolean = true,
    $includePeopleItems: Boolean = true,
    $includeDateItems: Boolean = true,
    $includeSelectItems: Boolean = true,
    $includeNumberItems: Boolean = true,
    $includeOtherItems: Boolean = true,
    $includeAdvancedItems: Boolean = true,
    $includeSuggestedItems: Boolean = true,
	$isNumberFieldConfigurableEnabled: Boolean = false
) {
  issueLayoutConfiguration(issueLayoutKey: {projectId: $projectId, extraDefinerId: $extraDefinerId}, type: $layoutType) {
    ... on QueryError {
      message
    }
    ... on JiraIssueLayoutConfigurationResult {
      metadata {
        __typename
        availableItems {
          restrictedFields(first: $pageSize, queryString: $queryString, after: $after) @include(if: $includeRestrictedItems) {
            ...CustomAvailableLayoutItemsGroup
          }
          suggestedFields(first: $pageSize, queryString: $queryString, after: $after) @include(if: $includeSuggestedItems) {
            ...CustomAvailableLayoutItemsGroup
          }
          systemAndAppFields(first: $pageSize, queryString: $queryString, after: $after) @include(if: $includeSystemItems)  {
            ...SystemAvailableLayoutItemsGroup
          }
          textFields(first: $pageSize, queryString: $queryString, after: $after) @include(if: $includeTextItems) {
            ...CustomAvailableLayoutItemsGroup
          }
          labelsFields(first: $pageSize, queryString: $queryString, after: $after) @include(if: $includeLabelsItems) {
            ...CustomAvailableLayoutItemsGroup
          }
          peopleFields(first: $pageSize, queryString: $queryString, after: $after) @include(if: $includePeopleItems) {
            ...CustomAvailableLayoutItemsGroup
          }
          dateFields(first: $pageSize, queryString: $queryString, after: $after) @include(if: $includeDateItems) {
            ...CustomAvailableLayoutItemsGroup
          }
          selectFields(first: $pageSize, queryString: $queryString, after: $after) @include(if: $includeSelectItems) {
            ...CustomAvailableLayoutItemsGroup
          }
          numberFields(first: $pageSize, queryString: $queryString, after: $after) @include(if: $includeNumberItems) {
            ...CustomAvailableLayoutItemsGroup
          }
          otherFields(first: $pageSize, queryString: $queryString, after: $after) @include(if: $includeOtherItems) {
            ...CustomAvailableLayoutItemsGroup
          }
          advancedFields(first: $pageSize, queryString: $queryString, after: $after) @include(if: $includeAdvancedItems) {
            ...CustomAvailableLayoutItemsGroup
          }
        }
      }
    }
  }
}

${AvailableLayoutItemGroupsFragments}
`;

type Includes = {
	includeRestrictedItems?: boolean;
	includeSystemItems?: boolean;
	includeTextItems?: boolean;
	includeLabelsItems?: boolean;
	includePeopleItems?: boolean;
	includeDateItems?: boolean;
	includeSelectItems?: boolean;
	includeNumberItems?: boolean;
	includeSuggestedItems?: boolean;
	includeOtherItems?: boolean;
	includeAdvancedItems?: boolean;
};

const includeEverything: Includes = {
	includeRestrictedItems: true,
	includeSystemItems: true,
	includeTextItems: true,
	includeLabelsItems: true,
	includePeopleItems: true,
	includeDateItems: true,
	includeSelectItems: true,
	includeNumberItems: true,
	includeSuggestedItems: true,
	includeOtherItems: true,
	includeAdvancedItems: true,
};

const includeNothing: Includes = {
	includeRestrictedItems: false,
	includeSystemItems: false,
	includeTextItems: false,
	includeLabelsItems: false,
	includePeopleItems: false,
	includeDateItems: false,
	includeSelectItems: false,
	includeNumberItems: false,
	includeSuggestedItems: false,
	includeOtherItems: false,
	includeAdvancedItems: false,
};

type IncludesByGroup = Record<Group, keyof Includes>;

const includesByGroup: IncludesByGroup = {
	[DATES_GROUP]: 'includeDateItems',
	[LABELS_GROUP]: 'includeLabelsItems',
	[NUMBER_GROUP]: 'includeNumberItems',
	[PEOPLE_GROUP]: 'includePeopleItems',
	[SELECT_GROUP]: 'includeSelectItems',
	[SYSTEM_GROUP]: 'includeSystemItems',
	[TEXT_GROUP]: 'includeTextItems',
	[SUGGESTED_GROUP]: 'includeSuggestedItems',
	[OTHER_GROUP]: 'includeOtherItems',
	[ADVANCED_GROUP]: 'includeAdvancedItems',
	[ASSETS_GROUP]: 'includeAdvancedItems',
	[VERSION_GROUP]: 'includeAdvancedItems',
	[AI_SUGGESTED_GROUP]: 'includeAdvancedItems',
	[RESTRICTED_GROUP]: 'includeRestrictedItems',
};

const fetchAvailableLayoutItemsInternal = (
	projectContext: ProjectContext,
	extraDefinerId: ExtraDefinerId,
	layoutType: string,
	pageSize: number,
	queryString: string,
	includes: Includes,
	after?: string,
): Promise<GraphqlResponse<AvailableIssueLayoutItems>> => {
	const client = getClient(toBaseUrl('')); // in cloud, baseUrl is always empty string
	const { projectId } = projectContext;

	return client.query({
		query: availableItems,
		variables: {
			projectId,
			extraDefinerId,
			layoutType,
			pageSize,
			queryString,
			fieldPropertyKeys: [],
			after,
			...includes,
		},
	});
};

export const fetchMoreAvailableLayoutItems =
	(layoutType: string) =>
	({
		projectContext,
		extraDefinerId,
		pageSize,
		group,
		queryString,
		after,
	}: FetchMoreAvailableItemsParams): Promise<ReadonlyArray<AvailableLayoutItemGroup>> => {
		const includes = { ...includeNothing };
		if (!includesByGroup[group]) {
			throw new Error(`Unknown layout items group: ${group}`);
		}

		includes[includesByGroup[group]] = true;

		return famaCatchAndLogFetchError<GraphqlResponse<AvailableIssueLayoutItems>>(
			fetchAvailableLayoutItemsInternal(
				projectContext,
				extraDefinerId,
				layoutType,
				pageSize,
				queryString,
				includes,
				after,
			),
			PACKAGE_NAME,
			toErrorId('fetchMoreAvailableLayoutItems'),
			toErrorMessage('Failed to fetch more available layout items in SWIFT'),
		).then(({ data }: GraphqlResponse<AvailableIssueLayoutItems>) => {
			const issueLayoutConfiguration = data?.issueLayoutConfiguration;
			if (
				!issueLayoutConfiguration ||
				issueLayoutConfiguration.__typename === 'QueryError' ||
				!issueLayoutConfiguration.metadata ||
				// @ts-expect-error - TS2367 - This condition will always return 'false' since the types '"JiraIssueLayoutMetadata"' and '"QueryError"' have no overlap.
				issueLayoutConfiguration.metadata.__typename === 'QueryError'
			) {
				throw new Error('Failed to fetch more available layout items');
			}

			return transformAvailableLayoutItemGroups(
				issueLayoutConfiguration.metadata.availableItems || {},
			);
		});
	};

export const fetchAvailableLayoutItems =
	(layoutType: string) =>
	({
		projectContext,
		extraDefinerId,
		pageSize,
		queryString,
	}: FetchAvailableItemsParams): Promise<ReadonlyArray<AvailableLayoutItemGroup>> =>
		famaCatchAndLogFetchError<GraphqlResponse<AvailableIssueLayoutItems>>(
			fetchAvailableLayoutItemsInternal(
				projectContext,
				extraDefinerId,
				layoutType,
				pageSize,
				queryString,
				includeEverything,
			),
			PACKAGE_NAME,
			toErrorId('fetchAvailableLayoutItems'),
			toErrorMessage('Failed to fetch available layout items in SWIFT'),
		).then(({ data }: GraphqlResponse<AvailableIssueLayoutItems>) => {
			const issueLayoutConfiguration = data?.issueLayoutConfiguration;
			if (
				!issueLayoutConfiguration ||
				issueLayoutConfiguration.__typename === 'QueryError' ||
				!issueLayoutConfiguration.metadata ||
				// @ts-expect-error - TS2367 - This condition will always return 'false' since the types '"JiraIssueLayoutMetadata"' and '"QueryError"' have no overlap.
				issueLayoutConfiguration.metadata.__typename === 'QueryError'
			) {
				throw new Error('Failed to fetch available layout items');
			}

			return transformAvailableLayoutItemGroups(
				issueLayoutConfiguration.metadata.availableItems || {},
			);
		});
